// Design Scope Matrix — shared types. See spec §3.

import type {
  Classification,
  ClassificationPathNode,
  Classifications
} from "./classification";

export type CsiSystem = "UF" | "MF";
export type Lod = 1 | 2 | 3 | 4 | 5;
export type ScopeStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "IN_REVIEW"
  | "COMPLETE";

export const LOD_LABELS: Record<Lod, string> = {
  1: "Identified",
  2: "Concept Defined",
  3: "Coordinated",
  4: "Detailed",
  5: "Complete"
};

/** Display / filter label, e.g. "L4 · Detailed". */
export function formatLodLabel(lod: Lod): string {
  return `L${lod} · ${LOD_LABELS[lod]}`;
}

/** One CSI taxonomy row. IndexedDB/CSV POC uses the full shape; live GraphQL
 *  search fills the core fields and leaves CSV-only fields unset. */
export interface CsiTerm {
  id: string; // catalog PK or `${system}:${number}` in the local POC
  system: CsiSystem;
  number: string; // "B30", "B3010.10", "07 31 13"
  title: string;
  /** GraphQL `parent_id` when sourced from classification_items. */
  parentId?: string | null;
  titleLower: string; // lowercased `title`, indexed for fast title-only search
  numberAndTitle: string;
  parentNumber: string | null; // derived from `number` (local CSI)
  depth: number; // derived from `number` (1-based)
  search: string; // lowercased `${number} ${title}`
  englishIncludes?: string;
  englishMayInclude?: string;
  englishDescription?: string;
  englishSynonyms?: string;
  englishDefinitions?: string;
  englishNotes?: string;
  englishDoesNotInclude?: string;
  englishDiscussion?: string;
  englishAlternateAbbreviations?: string;
  englishProducts?: string;
}

export type { Classification, ClassificationPathNode, Classifications };

// Kept as an alias so any straggling import still compiles; the ladder meaning
// is gone — an item now carries at most one code per standard.
export type HierarchyLevel = Classification;

export interface Commitment {
  designPackageId: string;
  milestoneId: string | null;
  /** 1–5 display sequence for meters / filters (derived from enum order). */
  lod: Lod;
  /** Backend `level_of_detail_enum_values.id` for create/update payloads. */
  levelOfDetailValueId: string;
  notes: string;
}

export interface BidAssociation {
  bidPackageId: string;
  role: string;
  primary: boolean;
}

export interface DsmScopeItem {
  id: string;
  projectId: string;
  /** Server-assigned display id from the list API (`sequence_id`). */
  sequence_id?: number;
  /** Absent when the item is unclassified. */
  classification?: Classifications;
  /** Human-friendly name. Falls back to the classification title when absent. */
  title?: string;
  description: string;
  status: ScopeStatus;
  /** Display name. Create/update sends `responsibleDesignerId` when the catalog id is known. */
  responsibility: string;
  responsibleDesignerId?: string;
  notes: string;
  commitments: Commitment[];
  bids: BidAssociation[];
  createdAt: string;
  updatedAt: string;
}

/** Create/update payload — everything except server-assigned fields. */
export type ScopeItemInput = Omit<
  DsmScopeItem,
  "id" | "projectId" | "createdAt" | "updatedAt" | "sequence_id"
>;

export type TemplateSource = "upload" | "project" | "mock";

/**
 * One reusable scope line. Templates carry classification + defaults ONLY —
 * commitments/bids/package ids are project-specific and must never leak into
 * a template (they'd dangle in any other project).
 */
export interface TemplateItem {
  classification?: Classifications;
  description: string;
  defaultLod?: Lod;
  responsibility?: string;
  status?: ScopeStatus;
}

/** Subscription-level (cross-project) scope template. */
export interface DsmTemplate {
  id: string;
  name: string;
  source: TemplateSource;
  items: TemplateItem[];
  createdAt: string;
}

export interface ScopeFilter {
  text?: string;
  top?: string;
  /** Which system `top` is a root code of. Defaults to UF. */
  topSystem?: CsiSystem;
  designPackageId?: string;
  milestoneId?: string;
  bidPackageId?: string;
  lod?: Lod;
}
